import {
  LitElement,
  html,
  nothing,
  svg,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { customElement, property } from "lit/decorators.js";
import { createActorState, type ActorStateOf } from "../../state/actor-state";
import { applyAccentVars } from "../../theme/theme";
import { confirmationStyles } from "./confirmation/confirmation.styles";
import { createEmbedAccount } from "../../data/live";
import { embeddedApiErrorStatus } from "../../data/embedded-api";
import { setEmbedToken } from "../../config/client";

type ConfirmKind = "class" | "appointment" | "purchase";

const HEADLINE: Record<ConfirmKind, string> = {
  class: "You're in!",
  appointment: "You're booked!",
  purchase: "You're all set!",
};

// Calendar provider icons (handoff colors).
const ICON_CAL = (color: string) =>
  svg`<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;
const ICON_PEOPLE = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
const ICON_SHARE = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`;
const ICON_STAR = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="none"><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z"/></svg>`;
const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
/** Inline confirmation tick — inherits the button's colour, unlike ICON_CHECK. */
const ICON_CHECK_SM = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

/**
 * The ONLY password rule the API enforces: `EmbedAuthService.createAccount`
 * (apps/api/src/embeddable-component/embed-auth.service.ts) rejects with 400
 * "Invalid account details" when the password is empty or shorter than this.
 * There is no complexity rule, no maximum, and neither side trims — so the
 * client must not add or relax rules, or it accepts input the server rejects
 * (or refuses input the server would have taken).
 */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Shown when the account-setup flow cannot work because the EMBED is wired
 * wrong. It deliberately does not ask the visitor to change anything: they
 * supplied nothing incorrect and cannot fix a missing binding on the host page.
 * It says what is true (their purchase is safe) and gives the one action that
 * can actually resolve it.
 */
const SETUP_UNAVAILABLE =
  "Account setup isn't available here yet — that's on us, not you. Your " +
  "booking is confirmed; contact the studio and they can finish setting up " +
  "your account.";

/** Feedback shown on the "Share with someone" button after it is pressed. */
type ShareState = "idle" | "shared" | "copied" | "failed";

const SHARE_LABEL: Record<ShareState, string> = {
  idle: "Share with someone",
  shared: "Shared!",
  copied: "Copied to clipboard!",
  failed: "Couldn't copy — here's the link",
};

/**
 * Colour and an icon swap are not perceivable to a screen-reader user, so the
 * same state change is also written into an `aria-live` region.
 */
const SHARE_ANNOUNCE: Record<ShareState, string> = {
  idle: "",
  shared: "Shared.",
  copied: "Invite copied to your clipboard.",
  failed:
    "Copying failed. The invite is shown below the button so you can copy it yourself.",
};

/** How long the transient "Copied!" / "Shared!" confirmation stays up. */
const SHARE_RESET_MS = 2200;

/** UTC stamp YYYYMMDDTHHMMSSZ for calendar links (from an ISO string). */
function stamp(iso: string): string {
  return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/** Name of a thrown DOMException/Error, tolerant of non-Error rejections. */
function errorName(err: unknown): string {
  return err instanceof Error ? err.name : "";
}

/**
 * Turn a failed `createEmbedAccount` call into something the visitor can act
 * on. The generated SDK exposes HTTP status through its typed `ResponseError`;
 * retain the legacy message fallback for callers/tests that still throw the
 * pre-SDK error shape.
 *
 * 401 in particular is the API's "no user/roster in this org for that email"
 * branch: retrying can never clear it, so answering it with "try again" walks
 * the visitor round a loop — the same defect class as blaming them for a
 * missing `orgId`. A typed error from `live.ts` would be the durable fix.
 */
function accountErrorMessage(err: unknown): string {
  const legacyStatus =
    err instanceof Error ? /→ (\d{3})$/.exec(err.message)?.[1] : undefined;
  const status = embeddedApiErrorStatus(err)?.toString() ?? legacyStatus;
  switch (status) {
    case "400":
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    case "401":
    case "404":
      return "We couldn't match that email to this booking. Contact the studio and they can set up your account.";
    case "429":
      return "Too many attempts. Wait a minute and try again.";
    default:
      return "Could not create account. Try again.";
  }
}

const confirmationActorState = createActorState(() => ({
  accountOpen: false,
  acctPassword: "",
  acctDone: false,
  shareState: "idle" as ShareState,
  acctBusy: false,
  acctError: "",
}));

type ConfirmationActorState = ActorStateOf<typeof confirmationActorState>;

/**
 * Shared post-checkout confirmation view — ported from the handoff
 * `confirmation.jsx`. Internal (not a registered embed); rendered inline by
 * <zipper-booking-checkout> and <zipper-purchase-checkout>. Inherits --zui-*
 * theme vars from the host embed's shadow tree.
 */
@customElement("zipper-confirmation")
export class ZipperConfirmation extends LitElement {
  static styles = confirmationStyles;

  @property({ type: String }) kind: ConfirmKind = "class";
  /** Embed accent — threaded so the success screen recolors off it. See X3. */
  @property({ type: String }) accent = "#4F9DB5";
  /**
   * REQUIRED whenever the account-setup block is offered — `createEmbedAccount`
   * posts to `/public/embeds/org/{orgId}/auth/create-account`, so there is no
   * request to make without it. It has no UI: if the parent embed forgets to
   * bind it the visitor has no way to supply or fix it, which is why a missing
   * value is reported as a configuration fault (see `missingSetupConfig`) and
   * never as user error.
   */
  @property({ type: Number }) orgId = 0;
  /** Also parent-supplied: the modal renders it readonly, never typeable. */
  @property({ type: String }) email = "";
  /**
   * PR11: the buyer's name is captured earlier in the checkout ("My details"
   * step) in both the class and purchase flows, so the account-setup modal no
   * longer re-asks for it — the parent threads it in and we submit it as-is.
   */
  @property({ type: String }) firstName = "";
  @property({ type: String }) lastName = "";
  @property({ type: String }) cTitle = "";
  @property({ type: String }) subline = "";
  @property({ type: String }) location = "";
  @property({ type: String }) price = "";
  @property({ type: String }) cardBrand = "";
  @property({ type: String }) cardLast4 = "";
  @property({ type: String }) orderId = "";
  /** ISO window for the calendar links (class/appointment only). */
  @property({ type: String }) startIso = "";
  @property({ type: String }) endIso = "";
  @property({ type: Boolean }) hideCalendar = false;
  @property({ type: Boolean }) hideBookAnother = false;
  @property({ type: Boolean }) hideShare = false;
  @property({ type: Boolean }) hideSetupAccount = false;

  /** Embedded "My Account" setup modal (opened from the setup CTA). */
  @confirmationActorState.field("accountOpen")
  private accountOpen!: ConfirmationActorState["accountOpen"];

  @confirmationActorState.field("acctPassword")
  private acctPassword!: ConfirmationActorState["acctPassword"];

  @confirmationActorState.field("acctDone")
  private acctDone!: ConfirmationActorState["acctDone"];

  @confirmationActorState.field("shareState")
  private shareState!: ConfirmationActorState["shareState"];

  private shareTimer?: ReturnType<typeof setTimeout>;
  /** Which missing property we have already logged, so we log it once. */
  private warnedSetupConfig: "orgId" | "email" | null = null;

  protected willUpdate(changed: PropertyValues): void {
    // Reflect the configured accent onto this host so `var(--zui-accent*)` used
    // by the success screen (checkmark circle, featured "Book another" panel,
    // "Set up account" CTA) matches the embed's accent instead of the baseStyles
    // teal default this component's own `:host` rule would otherwise pin. See X3.
    if (changed.has("accent")) applyAccentVars(this, this.accent);
    // Report a broken binding when the block is about to be OFFERED, not when
    // the visitor finally presses submit — by then it reads as their mistake.
    const missing = this.hideSetupAccount ? null : this.missingSetupConfig;
    if (missing) this.warnMissingSetupConfig(missing);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.shareTimer !== undefined) clearTimeout(this.shareTimer);
  }

  /**
   * Parent-supplied wiring the account-setup flow cannot work without. Neither
   * value is enterable by the visitor, so a missing one is a misconfiguration
   * of the embed — never bad user input, and never phrased as such.
   */
  private get missingSetupConfig(): "orgId" | "email" | null {
    if (!this.orgId) return "orgId";
    if (!this.email) return "email";
    return null;
  }

  /**
   * Developer-facing counterpart to `SETUP_UNAVAILABLE`. A misconfiguration
   * that only ever surfaces as a polite sentence to a customer is invisible to
   * the people who can fix it, so name the property and the call site here.
   */
  private warnMissingSetupConfig(missing: "orgId" | "email"): void {
    if (this.warnedSetupConfig === missing) return;
    this.warnedSetupConfig = missing;
    console.error(
      `[zipper-confirmation] Account setup is disabled: required property \`${missing}\` ` +
        `was not bound (received ${JSON.stringify(
          missing === "orgId" ? this.orgId : this.email,
        )}). ` +
        "The embed rendering <zipper-confirmation> must pass it, e.g. " +
        "`.orgId=${this.config?.orgId ?? 0}`. <zipper-booking-checkout> and " +
        "<zipper-purchase-checkout> do; any call site that omits it leaves " +
        "visitors permanently unable to create an account from this screen.",
    );
  }

  private emit(name: string): void {
    this.dispatchEvent(
      new CustomEvent(name, { bubbles: true, composed: true }),
    );
  }

  private googleUrl(): string {
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: this.cTitle,
      location: this.location,
    });
    if (this.startIso && this.endIso) {
      params.set("dates", `${stamp(this.startIso)}/${stamp(this.endIso)}`);
    }
    return `https://www.google.com/calendar/render?${params.toString()}`;
  }

  private icsUri(): string {
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `SUMMARY:${this.cTitle}`,
      this.location ? `LOCATION:${this.location}` : "",
      this.startIso ? `DTSTART:${stamp(this.startIso)}` : "",
      this.endIso ? `DTEND:${stamp(this.endIso)}` : "",
      "END:VEVENT",
      "END:VCALENDAR",
    ].filter(Boolean);
    return `data:text/calendar;charset=utf8,${encodeURIComponent(lines.join("\n"))}`;
  }

  render(): TemplateResult {
    const isEvent = this.kind !== "purchase";
    const showCal = isEvent && !this.hideCalendar;
    const showExtras = this.kind === "class";
    // AP-CONF: the "Book another" callout also applies to appointments (not just
    // classes) — both kinds return to their embed's first screen for a fresh
    // booking. Only the label differs; the "Share" callout stays class-only.
    const showBookAnother = isEvent && !this.hideBookAnother;
    const bookAnotherLabel =
      this.kind === "appointment"
        ? "Book another appointment"
        : "Return to Schedule — Book another class";
    const ics = isEvent ? this.icsUri() : "#";
    return html`
      <div class="wrap">
        <div class="hero-check">${ICON_CHECK}</div>
        <h1 class="headline">${HEADLINE[this.kind]}</h1>
        ${this.email
          ? html`<div class="receipt">
              We sent a receipt to <strong>${this.email}</strong>.
            </div>`
          : nothing}

        <div class="card">
          <div class="card-top">
            <span class="card-title">${this.cTitle}</span>
            ${this.price
              ? html`<span class="card-price">${this.price}</span>`
              : nothing}
          </div>
          ${this.subline
            ? html`<div class="card-sub">${this.subline}</div>`
            : nothing}
          ${this.location
            ? html`<div class="card-sub">${this.location}</div>`
            : nothing}
          ${this.cardLast4 || this.orderId
            ? html`<div class="card-foot">
                <span>
                  ${this.cardLast4
                    ? `Charged to ${this.cardBrand || "card"} ····${this.cardLast4}`
                    : ""}
                </span>
                <span>${this.orderId ? `Order #${this.orderId}` : ""}</span>
              </div>`
            : nothing}
        </div>

        ${showCal
          ? html`<div class="cal-grid">
              <a
                class="cal-btn"
                href=${this.googleUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                ${ICON_CAL("#4285F4")} Add to Google
              </a>
              <a class="cal-btn" href=${ics} download="event.ics">
                ${ICON_CAL("#000000")} Add to Apple
              </a>
              <a class="cal-btn" href=${ics} download="event.ics">
                ${ICON_CAL("#0078D4")} Add to Outlook
              </a>
            </div>`
          : nothing}
        ${showBookAnother
          ? html`<button
              class="wide-btn soft"
              @click=${() => this.emit("zipper-confirm-book-another")}
            >
              ${ICON_PEOPLE} ${bookAnotherLabel}
            </button>`
          : nothing}
        ${showExtras && !this.hideShare ? this.renderShare() : nothing}
        ${this.hideSetupAccount ? nothing : this.renderSetup()}
      </div>
      ${this.accountOpen ? this.renderAccountModal() : nothing}
    `;
  }

  /**
   * The post-checkout account pitch.
   *
   * When the embed is misconfigured the CTA is DISABLED WITH THE REASON VISIBLE
   * rather than hidden. Hiding it would make a wiring bug indistinguishable
   * from normal behaviour — no visitor, studio owner or engineer would ever
   * learn the feature was dead, which is precisely how the missing `orgId`
   * binding shipped in the first place. The visitor gives up nothing by seeing
   * it: their booking is already complete and this panel is a follow-on offer,
   * not a step. So the honest, reportable version wins. Either way the modal is
   * never opened, so there is no working-looking form that can only fail.
   */
  private renderSetup(): TemplateResult {
    const blocked = this.missingSetupConfig !== null;
    return html`<div class="setup">
      <div class="h">${ICON_STAR} First time booking with us? Welcome!</div>
      <div class="b">
        Save your info for next time. See your bookings, manage payments, and
        skip the forms.
      </div>
      <button
        class="cta"
        type="button"
        ?disabled=${blocked}
        @click=${() => this.openAccount()}
      >
        Set up account
      </button>
      ${blocked
        ? html`<p class="setup-note" role="note">${SETUP_UNAVAILABLE}</p>`
        : nothing}
    </div>`;
  }

  /**
   * "Share with someone". This used to only emit `zipper-confirm-share` and
   * leave the actual sharing to the parent embed — which <zipper-events> never
   * listens for, so on the events path the button did nothing whatsoever. The
   * behaviour now lives here so it works from every call site; the event is
   * still dispatched for parents that observe it.
   */
  private renderShare(): TemplateResult {
    const state = this.shareState;
    const done = state === "copied" || state === "shared";
    return html`
      <button
        class="wide-btn plain share-btn ${state}"
        type="button"
        @click=${() => void this.share()}
      >
        ${done ? ICON_CHECK_SM : ICON_SHARE} ${SHARE_LABEL[state]}
      </button>
      <p class="share-live" role="status" aria-live="polite">
        ${SHARE_ANNOUNCE[state]}
      </p>
      ${state === "failed"
        ? html`<div class="share-fallback">
            <label class="acct-field"
              >Copy this and send it to a friend
              <input
                type="text"
                readonly
                .value=${this.shareMessage()}
                @focus=${(e: Event) => (e.target as HTMLInputElement).select()}
              />
            </label>
          </div>`
        : nothing}
    `;
  }

  /**
   * The link a friend should land on. The platform has no canonical per-event
   * URL — the page hosting this embed IS the registration page — so we share
   * that page's URL with any fragment stripped, matching what <zipper-events>'
   * own "Share" action builds (zipper-events.ts#shareEvent).
   */
  private shareLink(): string {
    const here =
      typeof window !== "undefined" ? window.location.href.split("#")[0] : "";
    // `about:blank` / `about:srcdoc` mean we are inside a generated iframe (the
    // admin preview); the embedding page is the only useful link there.
    if (here && !here.startsWith("about:")) return here;
    return typeof document !== "undefined" ? document.referrer : "";
  }

  /** The human line that travels with the link. */
  private shareHeadline(): string {
    const title = this.cTitle.trim();
    return title
      ? `Check out this event I'm going to — ${title}!`
      : "Check out this event I'm going to!";
  }

  /** Headline + link, as put on the clipboard (the share sheet takes them apart). */
  private shareMessage(): string {
    const url = this.shareLink();
    return url ? `${this.shareHeadline()} ${url}` : this.shareHeadline();
  }

  private async share(): Promise<void> {
    // Kept for parents that observe the action (analytics, their own sharing).
    this.emit("zipper-confirm-share");

    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav?.share) {
      try {
        await nav.share({
          title: this.cTitle || "Join me",
          text: this.shareHeadline(),
          url: this.shareLink(),
        });
        this.flashShare("shared");
        return;
      } catch (err) {
        const name = errorName(err);
        // Dismissing the sheet is a decision, not a failure — copying the
        // invite anyway would push something they just declined to send.
        if (name === "AbortError") return;
        // A parent embed handling `zipper-confirm-share` opened its own sheet
        // first; don't stack a clipboard write on top of it.
        if (name === "InvalidStateError") return;
        // Anything else (no share target, blocked by permissions policy) is
        // exactly what the clipboard fallback exists for.
      }
    }
    await this.copyShare();
  }

  private async copyShare(): Promise<void> {
    try {
      // `navigator.clipboard` is absent outside a secure context and
      // `writeText` rejects when the host page's permissions policy blocks it.
      // Both are ordinary on a third-party page, so neither may end in a button
      // that silently does nothing — that is the bug this replaces.
      const clip =
        typeof navigator !== "undefined" ? navigator.clipboard : undefined;
      if (!clip?.writeText) throw new Error("clipboard unavailable");
      await clip.writeText(this.shareMessage());
      this.flashShare("copied");
    } catch {
      // Reveal the text instead, so the visitor can copy it by hand.
      this.setShareState("failed");
    }
  }

  private setShareState(state: ShareState): void {
    if (this.shareTimer !== undefined) {
      clearTimeout(this.shareTimer);
      this.shareTimer = undefined;
    }
    this.shareState = state;
  }

  /**
   * Show a transient confirmation, then return the button to its idle label.
   * `failed` deliberately does not use this — its fallback text has to stay put
   * long enough to be copied.
   */
  private flashShare(state: ShareState): void {
    this.setShareState(state);
    this.shareTimer = setTimeout(() => {
      this.shareTimer = undefined;
      this.shareState = "idle";
    }, SHARE_RESET_MS);
  }

  private openAccount(): void {
    // Defence in depth: the CTA is already disabled when the embed is
    // misconfigured, so a form that can only fail never gets rendered.
    if (this.missingSetupConfig) return;
    this.acctDone = false;
    // Reset per-attempt state, or a failure from a previous open is still on
    // screen when the modal is re-opened with an empty password box.
    this.acctError = "";
    this.acctPassword = "";
    this.accountOpen = true;
  }

  private closeAccount(): void {
    this.accountOpen = false;
  }

  /**
   * Matches the server rule exactly — see MIN_PASSWORD_LENGTH. Kept as the
   * single definition of "valid" so the button state and the submit-time
   * message can never disagree.
   */
  private get acctValid(): boolean {
    return this.acctPassword.length >= MIN_PASSWORD_LENGTH;
  }

  @confirmationActorState.field("acctBusy")
  private acctBusy!: ConfirmationActorState["acctBusy"];

  @confirmationActorState.field("acctError")
  private acctError!: ConfirmationActorState["acctError"];

  private async submitAccount(): Promise<void> {
    if (this.acctBusy) return;

    // Configuration, not user input: say so honestly and log the real cause.
    const missing = this.missingSetupConfig;
    if (missing) {
      this.warnMissingSetupConfig(missing);
      this.acctError = SETUP_UNAVAILABLE;
      return;
    }

    // Genuine user input — name the field and the rule that was not met.
    if (!this.acctValid) {
      this.acctError = this.acctPassword
        ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
        : "Enter a password to create your account.";
      return;
    }

    this.acctBusy = true;
    this.acctError = "";
    try {
      const res = await createEmbedAccount(this.orgId, {
        email: this.email,
        password: this.acctPassword,
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim(),
      });
      setEmbedToken(this.orgId, res.token);
      this.dispatchEvent(
        new CustomEvent("zipper-confirm-account-setup", {
          detail: { token: res.token, firstName: res.firstName },
          bubbles: true,
          composed: true,
        }),
      );
      this.acctDone = true;
    } catch (err) {
      this.acctError = accountErrorMessage(err);
    } finally {
      this.acctBusy = false;
    }
  }

  /**
   * "Create account" is only disabled while a request is in flight. Greying it
   * out for a short password explained nothing — the visitor was left with a
   * dead control and no idea which rule they had missed. Letting the press
   * through produces a message that names the field and the rule instead.
   */
  private renderAccountModal(): TemplateResult {
    return html`
      <div
        class="acct-overlay"
        @click=${(e: Event) =>
          e.target === e.currentTarget && this.closeAccount()}
      >
        <div
          class="acct-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Set up your account"
        >
          ${this.acctDone
            ? html`<div class="acct-done">
                <div class="hero-check" style="width:52px;height:52px">
                  ${ICON_CHECK}
                </div>
                <h3>You're all set</h3>
                <div class="acct-sub">
                  We saved your details. Check
                  <strong>${this.email || "your inbox"}</strong> for a welcome
                  email.
                </div>
                <button class="acct-save" @click=${() => this.closeAccount()}>
                  Done
                </button>
              </div>`
            : html` <h3>Set up your account</h3>
                <div class="acct-sub">
                  Save your info for next time — see your bookings, manage
                  payments, and skip the forms.
                </div>
                <label class="acct-field"
                  >Email
                  <input type="email" .value=${this.email} readonly />
                </label>
                <label class="acct-field"
                  >Create a password
                  <input
                    type="password"
                    autocomplete="new-password"
                    placeholder=${`At least ${MIN_PASSWORD_LENGTH} characters`}
                    .value=${this.acctPassword}
                    @input=${(e: Event) => {
                      this.acctPassword = (e.target as HTMLInputElement).value;
                      // Clear stale feedback as they correct it.
                      if (this.acctError) this.acctError = "";
                    }}
                  />
                </label>
                ${this.acctError
                  ? html`<div class="acct-error" role="alert">
                      ${this.acctError}
                    </div>`
                  : nothing}
                <div class="acct-actions">
                  <button
                    class="acct-cancel"
                    @click=${() => this.closeAccount()}
                  >
                    Cancel
                  </button>
                  <button
                    class="acct-save"
                    ?disabled=${this.acctBusy}
                    @click=${() => this.submitAccount()}
                  >
                    ${this.acctBusy ? "Creating…" : "Create account"}
                  </button>
                </div>`}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-confirmation": ZipperConfirmation;
  }
}
